import { DateTime, Duration } from "luxon";

import type { AssignmentIntervalPage, AssignmentIntervalQuery } from "@storyboard/web-client";
import { ApiError, listAssignmentIntervals, serverNow } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { upsertAssignmentIntervals } from "@/models/repositories/AssignmentInterval";
import { registryStateStore, type SyncStateStore } from "@/models/sync/cursorStore";
import type { SyncContext } from "@/models/sync/syncTargets";
import { TMS_EVENT_BOOTSTRAP_DEPTH } from "@/models/tmsEvent/tmsEventSync";
import { recordEvent } from "@/services/telemetry";

const log = createLogger("appsync");

/** The longest span one history request may cover; the server refuses a wider one, and a delta cursor older than it. */
export const ASSIGNMENT_WINDOW_MAX = Duration.fromObject({ days: 31 });

/**
 * How far before the boot's oldest TMS event its records are read from, so an event that occurred a while before it was
 * created still finds the record it falls in.
 */
export const ASSIGNMENT_HISTORY_MARGIN = Duration.fromObject({ days: 1 });

/** Registry key of the delta cursor: the `cursor` the last read reaching the present returned. */
export const ASSIGNMENT_CURSOR_KEY = "assignmentIntervals";

/** Registry key of the oldest instant from which the store holds every record, up to the present. */
export const ASSIGNMENT_FETCHED_FROM_KEY = "assignmentIntervals:fetchedFrom";

/** A span of time, as ISO instants, both ends inclusive. */
type AssignmentWindow = { from: string; to: string };

/** The collaborators the sync reads and writes through; production binds the transport, the registry and the store. */
export type AssignmentIntervalSyncDeps = {
  fetchPage: (query: AssignmentIntervalQuery) => Promise<AssignmentIntervalPage>;
  state: SyncStateStore;
  apply: (page: AssignmentIntervalPage) => void;
  /** A recoverable condition worth a reliability event, such as a cursor the server refused. */
  report: (name: string, attributes: Record<string, unknown>) => void;
  /** The server's clock as the client estimates it. */
  now: () => Date;
};

export type AssignmentIntervalSync = {
  /** Reads the records changed since the delta cursor; a refused or missing cursor reads the held span instead. */
  delta: (ctx: SyncContext) => Promise<void>;
};

/**
 * Binds the assignment record reads to their cursor and fetched span.
 *
 * Every read of a workspace runs after the one before it, so each sees the cursor and span its predecessor left. Every
 * window a read requests starts no older than `ASSIGNMENT_WINDOW_MAX` before the `now` it ends at, so the server
 * serves each in one request.
 */
export function createAssignmentIntervalSync(deps: AssignmentIntervalSyncDeps): AssignmentIntervalSync {
  const queues = new Map<string, Promise<void>>();

  /** Runs `read` after every read of the workspace already queued; a failed read never blocks the next. */
  const serially = (workspaceId: string, read: () => Promise<void>): Promise<void> => {
    const queued = (queues.get(workspaceId) ?? Promise.resolve()).catch(() => undefined).then(read);
    queues.set(workspaceId, queued);
    return queued;
  };

  const iso = (instant: DateTime): string => instant.toJSDate().toISOString();

  /** The span the boot's TMS events cover as of `now`, with the margin before it. */
  const bootWindow = (now: DateTime): AssignmentWindow => ({
    from: iso(now.minus({ hours: TMS_EVENT_BOOTSTRAP_DEPTH.hours }).minus(ASSIGNMENT_HISTORY_MARGIN)),
    to: iso(now),
  });

  /**
   * The start of the span the store holds, never older than the server keeps as of `now`; undefined when nothing is
   * held.
   */
  const heldFrom = async (workspaceId: string, now: DateTime): Promise<DateTime | undefined> => {
    const fetchedFrom = await deps.state.get(ASSIGNMENT_FETCHED_FROM_KEY, workspaceId);
    if (fetchedFrom === undefined) return undefined;
    return DateTime.max(DateTime.fromISO(fetchedFrom), now.minus(ASSIGNMENT_WINDOW_MAX));
  };

  /**
   * Reads one window ending now, lands its records, and records the span the store now holds.
   *
   * The window ends now, so its read's cursor is where the next delta resumes: no record opened after it can be missed.
   *
   * @param ctx - The workspace read.
   * @param window - The span read, ending at `now`.
   * @param now - The instant the window was measured from, against which the held span is kept within the server's.
   */
  const readHistory = async ({ workspaceId }: SyncContext, window: AssignmentWindow, now: DateTime): Promise<void> => {
    const page = await deps.fetchPage({ workspaceId, ...window });
    deps.apply(page);
    await deps.state.set(ASSIGNMENT_CURSOR_KEY, workspaceId, page.cursor);

    // The store now holds every record from the window's start, joined to the span it held, which never reaches
    // back past what the server keeps.
    const held = await heldFrom(workspaceId, now);
    const from = DateTime.fromISO(window.from);
    await deps.state.set(
      ASSIGNMENT_FETCHED_FROM_KEY,
      workspaceId,
      iso(held === undefined || from < held ? from : held),
    );
  };

  /** The span the store claims to hold as of `now`, or on a cold boot the boot's, read whole when no delta can run. */
  const heldWindow = async (workspaceId: string, now: DateTime): Promise<AssignmentWindow> => {
    const held = await heldFrom(workspaceId, now);
    return held === undefined ? bootWindow(now) : { from: iso(held), to: iso(now) };
  };

  const readDelta = async (ctx: SyncContext): Promise<void> => {
    const now = DateTime.fromJSDate(deps.now());
    const since = await deps.state.get(ASSIGNMENT_CURSOR_KEY, ctx.workspaceId);
    // No cursor yet: nothing to continue from, so read the held span, which leaves one.
    if (since === undefined) return readHistory(ctx, await heldWindow(ctx.workspaceId, now), now);

    let page: AssignmentIntervalPage;
    try {
      page = await deps.fetchPage({ workspaceId: ctx.workspaceId, since });
    } catch (error) {
      // The route answers 400 for a cursor older than it serves; any other failure is the caller's to retry.
      if (!(error instanceof ApiError && error.status === 400)) throw error;
      deps.report("assignmentIntervals.cursor_refused", { assignmentIntervals: { since } });
      return readHistory(ctx, await heldWindow(ctx.workspaceId, now), now);
    }
    deps.apply(page);
    await deps.state.set(ASSIGNMENT_CURSOR_KEY, ctx.workspaceId, page.cursor);
  };

  return {
    delta: (ctx) => serially(ctx.workspaceId, () => readDelta(ctx)),
  };
}

const production = createAssignmentIntervalSync({
  fetchPage: (query) => listAssignmentIntervals(transport, query),
  state: registryStateStore,
  apply: upsertAssignmentIntervals,
  report: (name, attributes) => recordEvent(name, attributes, "reliability"),
  now: serverNow,
});

/**
 * Delta since the stored cursor; upserts; advances the cursor.
 *
 * A 400 (cursor too old), or no cursor yet, falls back to a history read over the visible window: the span the store
 * holds, or on a cold boot the span the boot's TMS events cover.
 */
export async function fetchAssignmentDelta(ctx: SyncContext): Promise<void> {
  await production.delta(ctx);
}

/**
 * Brings the records current at workspace boot: one delta on a warm boot, the boot's history window on a cold one.
 *
 * A failure is logged and left to the next delta.
 */
export async function bootstrapAssignmentIntervals(ctx: SyncContext): Promise<void> {
  try {
    await production.delta(ctx);
  } catch (error) {
    log.warn("[sync] Assignment interval bootstrap failed; the next delta retries", { error });
  }
}
